import { InjectionContext } from './injection-context';
import { Injection } from './injection';
import { WebFormInjection } from './web-form-injection';
import { PortalInjection } from './portal-injection';
import './html-element-extensions';
import { WebFormEmbedOptions } from './models/web-form-embed-options';
import { PortalEmbedOptions } from './models/portal-embed-options';
import { InterFrameMessage } from './models/inter-frame-message';
import { UrlHelper } from './helpers/url.helper';
import { EmbedOptionsResolver } from './helpers/embed-options-resolver';

let _uBindInjector: UBindInjector | null = null;

/**
 * The uBind injector embeds web forms and portals into web pages and apps
 */
class UBindInjector {

    private injectionContext: InjectionContext = new InjectionContext();
    private injections: Map<string, Injection> = new Map<string, Injection>();
    public debug: boolean = false;

    // Google Tag Manager related
    protected dataLayer: any;
    protected tagManagerIsEnabled: boolean = false;
    protected trackedFormModelValues: Array<string> | null = null;
    protected trackedCalculationValues: Array<string> | null = null;

    public constructor() {
        this.injectMaterialPreloaderStylesheet();
        window.addEventListener(
            "message",
            (event: any) => this.handleMessageEvent(event),
            false);
        window.addEventListener(
            "scroll",
            (event: any) => this.handleScrollEvent(event),
            false);
        window.addEventListener(
            "resize",
            (event: any) => this.handleResizeEvent(event),
            false);
    }

    public detectAndInject(): void {
        const element: HTMLElement | null = document.getElementById('ubindFrameContainer');
        if (element) {
            element.addEventListener(
                "scroll",
                (event: any) => this.handleScrollEvent(event),
                false);
        }
        this.startWhenDocumentIsReady();
        this.checkInsertDeviceViewPort();
        this.removeToken();
    }

    private injectMaterialPreloaderStylesheet(): void {
        const head: HTMLHeadElement = document.head;
        const link: HTMLLinkElement = document.createElement("link");

        link.type = "text/css";
        link.rel = "stylesheet";
        link.href = this.injectionContext.uBindAssetUrl + 'material-preloader.css';
        head.appendChild(link);
    }

    private checkInsertDeviceViewPort(): void {
        const meta: HTMLMetaElement = document.createElement('meta');
        const metaVieport: HTMLElement | null = document.head.querySelector("[name=viewport]");
        const canInject: boolean = !metaVieport || metaVieport.tagName != "meta";
        if (canInject) {
            meta.setAttribute('name', 'viewport');
            meta.setAttribute('injected', 'true');
            meta.content = "width=device-width, initial-scale=1.0";
            document.getElementsByTagName('head')[0].appendChild(meta);
        }
    }

    private handleCloseApp(origin: any, data: any): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        injection?.handleCloseApp(origin, data);

        // wait half a second before removing, so that animations can complete
        setTimeout(() => {
            this.removeInjectedIframe(data.frameId);
        }, 500);
    }

    public removeInjectedIframe(frameId: string): void {
        this.injections.delete(frameId);
        const iframeElement: HTMLElement | null = document.getElementById(frameId);
        if (iframeElement) {
            iframeElement.remove();
        }
    }

    /* eslint-disable no-fallthrough */
    public handleMessageEvent(event: any): void {
        if (event.origin === this.injectionContext.uBindAppHost) {
            if (event.data?.messageType) {
                if (this.debug) {
                    console.log('InjectionApp received message: ', event.data.messageType);
                }
            }
            switch (event.data.messageType) {
                case 'frameLoading':
                    this.handleFrameLoadingEvent(event.data);
                    break;
                case 'appLoad':
                    this.handleAppLoadEvent(event.data);
                    break;
                case 'configurationLoaded':
                    this.handleConfigurationLoadedEvent(event.data);
                    break;
                case 'webFormLoad':
                    this.handleWebFormLoadEvent(event.data);
                    break;
                case 'formSessionReady':
                    this.handleFormSessionReadyEvent(event.data);
                case 'scrollToElement':
                    this.handleScrollToElementEvent(event.data);
                    break;
                case 'closeApp':
                    this.handleCloseApp(event.origin, event.data);
                    break;
                case 'appEvent':
                    this.handleAppEvent(event.data);
                    break;
                case 'saveInitiated':
                case 'policyInitiated':
                case 'closeButtonLabel':
                case 'authenticationError':
                case 'quoteStateChanged':
                case 'claimStateChanged':
                    if (window.top != window.self) {
                        window.parent.postMessage(event.data, event.origin);
                    }
                    break;
                case 'displayMessage':
                    this.handleDisplayMessageEvent(event.data);
                    break;
                case 'redirect':
                    this.handleRedirect(event.data);
                case 'urlChanged':
                    this.handleUrlChangedEvent(event.data);
                    break;
                case 'policy-issued':
                case 'policy-adjusted':
                case 'policy-renewed':
                case 'policy-cancelled':
                    this.handleWebFormPolicyTransactionEvent(event.origin, event.data);
                    break;
                case 'page-changed':
                    this.handleWebFormPageChangeEvent(event.origin, event.data);
                    break;
                default:
                    break;
            }
        }
    }

    public handleAppEvent(data: any): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (injection) {
            const newEvent: CustomEvent = new CustomEvent(data.payload.eventType, { detail: data.payload.data });
            injection.rootElement.dispatchEvent(newEvent);
        } else {
            console.log('Received an appEvent for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        }
        this.forwardEventToGoogleTagManager(data);
    }

    public forwardEventToGoogleTagManager(data: any): void {
        if (this.tagManagerIsEnabled) {
            const tagManagerData: any = {
                'event': 'uBind.' + data['payload']['eventType'],
            };
            if (data['payload']['eventData']) {
                tagManagerData['uBindEventData'] = data['payload']['eventData'];
            }
            if (data['payload']['formModel']) {
                tagManagerData['uBindFormModel'] = data['payload']['formModel'];
            }
            if (data['payload']['calculation']) {
                tagManagerData['uBindCalculation'] = data['payload']['calculation'];
            }
            this.dataLayer.push(tagManagerData);
        }
    }

    public handleScrollEvent(event: any): void {
        this.injections.forEach((injection: Injection) => injection.handleScrollEvent(event));
    }

    public handleResizeEvent(event: any): void {
        this.injections.forEach((injection: Injection) => injection.handleResizeEvent(event));
    }

    private handleRedirect(data: any): void {
        if (data.payload.url) {
            window.location.href = data.payload.url;
        }
    }

    private handleDisplayMessageEvent(data: any): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (injection) {
            injection.displayMessage(data.payload.message, data.payload.severity ? data.payload.severity : 3);
        } else {
            console.log('Received a display message event for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        }
    }

    private handleUrlChangedEvent(data: any): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (injection && injection instanceof PortalInjection) {
            (<PortalInjection>injection).updateUrlPathQueryParameter(data.payload);
        } else {
            console.log('Received a url changed event for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        }
    }

    private startWhenDocumentIsReady(): void {
        if (document.readyState != 'complete') {
            setTimeout(
                () => {
                    this.startWhenDocumentIsReady();
                },
                this.injectionContext.checkReadyIntervalMs);
        } else {
            this.setupGoogleTagManager();
            this.createInjections();
        }
    }

    /**
     * This sets up google analytics.
     */
    private setupGoogleTagManager(): void {
        const dataLayer: any | null = (<any>window)['dataLayer'] || null;
        if (dataLayer && dataLayer['push']) {
            this.tagManagerIsEnabled = true;
            this.dataLayer = dataLayer;
            const uBindTagManagerTrackProperties: any | null = (<any>window)['uBindTagManagerTrackProperties'] || null;
            if (uBindTagManagerTrackProperties) {
                this.trackedFormModelValues = uBindTagManagerTrackProperties['formFields'] || [];
                this.trackedCalculationValues = uBindTagManagerTrackProperties['calculation'] || [];
            }
        }
    }

    private createInjections(): void {
        this.createWebFormInjections();
        this.createPortalInjections();
        document.addEventListener('keydown', this.onXKeydownRevealIFrame.bind(this));
    }

    private createWebFormInjections(): void {
        const elements: HTMLCollectionOf<Element> = document.getElementsByClassName('ubind-product');
        for (const element of Array.from(elements) as Array<HTMLElement>) {
            const formId: string = this.createFormInjection(element);
            const autoEmbed: boolean = element.getBooleanAttributeValue('data-auto-embed', true);
            const autoLoad: boolean = element.getBooleanAttributeValue('data-auto-load', true);
            const autoStart: boolean = element.getBooleanAttributeValue('data-auto-start', true);
            if (autoStart && autoLoad && autoEmbed) {
                this.startForm(formId);
            } else if (autoLoad && autoEmbed) {
                this.loadForm(formId);
            } else if (autoEmbed) {
                this.embedForm(formId);
            }
        }
    }

    private createFormInjection(element: HTMLElement, embedOptions?: WebFormEmbedOptions): string {
        if (!embedOptions) {
            embedOptions = <WebFormEmbedOptions>{};
        }
        const parentUrl: string = this.getParentUrl();
        const isLoadedWithinPortal: boolean = element.getBooleanAttributeValue('data-isLoadedWithinPortal', false);
        EmbedOptionsResolver.resolve(element, embedOptions);
        const injection: WebFormInjection = new WebFormInjection(
            element,
            embedOptions,
            parentUrl,
            isLoadedWithinPortal,
            this.injectionContext);
        const frameId: string = injection.getIframeId();
        this.injections.set(frameId, injection);
        element.setAttribute('data-injection-id', frameId);
        return frameId;
    }

    public createPopupDiv(): HTMLDivElement {
        const divEl: HTMLDivElement = document.createElement('div');
        divEl.id = 'embed-location';
        document.body.appendChild(divEl);
        return divEl;
    }

    private getFormId(element: HTMLElement): string | null {
        return element.getAttribute('data-injection-id');
    }

    /**
     * Creates a form at the specified HTML element.
     * Returns a unique formId, that can be used when calling further embed initialisation functions on this
     * form instance.
     * This does not result in the app's bundle being downloaded to the browser, unless the autoEmbed option
     * is set to true.
     */
    public createFormElement(element: HTMLElement, embedOptions?: WebFormEmbedOptions): string {
        const formId: string = this.createFormInjection(element, embedOptions);
        embedOptions = EmbedOptionsResolver.resolve(element, embedOptions);
        embedOptions.autoEmbed ??= true;
        embedOptions.autoLoad ??= true;
        embedOptions.autoStart ??= true;
        if (embedOptions.autoStart && embedOptions.autoLoad && embedOptions.autoEmbed) {
            this.startForm(formId, embedOptions);
        } else if (embedOptions.autoLoad && embedOptions.autoEmbed) {
            this.loadForm(formId, embedOptions);
        } else if (embedOptions.autoEmbed) {
            this.embedForm(formId, embedOptions);
        }
        return formId;
    }

    /**
     * Creates a form as a popup modal over the page, and returns and the ID of the form element.
     * Returns a unique formId, that can be used when calling further embed initialisation functions on this
     * form instance.
     * This does not result in the app's bundle being downloaded to the browser, unless the autoEmbed option
     * is set to true.
     */
    public createFormPopup(embedOptions: WebFormEmbedOptions): string {
        const divEl: HTMLDivElement = this.createPopupDiv();
        embedOptions.modalPopup = true;
        return this.createFormElement(divEl, embedOptions);
    }

    /**
     * Embeds a form at the specified html element.
     * Returns a unique formId, that can be used when calling further embed initialisation functions on this
     * form instance.
     * This results in the app's bundle being downloaded to the browser so that when the session is ready to
     * start, it will be faster.
     * This does not result in the form being initialised, unless the autoInit embed option is set to true.
     */
    public embedFormElement(element: HTMLElement, embedOptions?: WebFormEmbedOptions): string {
        const formId: string = this.getFormId(element)
            ?? this.createFormInjection(element, embedOptions);
        this.embedForm(formId, embedOptions);
        return formId;
    }

    /**
     * Embeds a form as a popup modal over the page, and returns and the ID of the form element.
     * Returns a unique formId, that can be used when calling further embed initialisation functions on this
     * form instance.
     * This results in the app's bundle being downloaded to the browser so that when the session is ready to
     * start, it will be faster.
     * This does not result in the form being initialised, unless the autoInit embed option is set to true.
     */
    public embedFormPopup(embedOptions: WebFormEmbedOptions): string {
        const divEl: HTMLDivElement = this.createPopupDiv();
        embedOptions.modalPopup = true;
        return this.embedFormElement(divEl, embedOptions);
    }

    /**
     * Embeds a form with the specified formId.
     * The embedding process involves downloading the app's bundle files to the browser.
     * This does not result in the form being initialised, unless the autoInit embed option is set to true.
     */
    public embedForm(formId: string, embedOptions?: WebFormEmbedOptions): void {
        const injection: WebFormInjection = this.getWebFormInjectionOrThrow(formId);
        embedOptions = EmbedOptionsResolver.resolve(injection.rootElement, embedOptions);
        embedOptions.autoEmbed ??= true;
        injection.updateEmbedOptions(embedOptions);
        injection.embed();
    }

    /**
     * Loads the quote or claim for the form at the specified html element, and the associated form configuration.
     * If this is for a new quote or claim, the quote or claim is created.
     * Returns a unique formId, that can be used when calling further embed initialisation functions on this form
     * instance.
     * If the app's bundle has not yet been downloaded, it will be downloaded first.
     * This does not result in the form session being started, unless the autoStart embed option is set to true.
     */
    public loadFormElement(element: HTMLElement, embedOptions?: WebFormEmbedOptions): string {
        const formId: string = this.getFormId(element)
            ?? this.createFormInjection(element, embedOptions);
        this.loadForm(formId, embedOptions);
        return formId;
    }

    /**
     * Loads the quote or claim a popup form, along with the associated form configuration.
     * If this is for a new quote or claim, the quote or claim is created.
     * If the app's bundle has not yet been downloaded, it will be downloaded first.
     * This does not result in the form session being started, unless the autoStart embed option is set to true.
     */
    public loadFormPopup(embedOptions: WebFormEmbedOptions): string {
        const divEl: HTMLDivElement = this.createPopupDiv();
        embedOptions.modalPopup = true;
        return this.loadFormElement(divEl, embedOptions);
    }

    /**
     * Loads the quote or claim for a form with the specified formId, along with the associated form configuration.
     * If this is for a new quote or claim, the quote or claim is created.
     * If the app's bundle has not yet been downloaded, it will be downloaded first.
     * This does not result in the form session being started, unless the autoStart embed option is set to true.
     */
    public loadForm(formId: string, embedOptions?: WebFormEmbedOptions): void {
        const injection: WebFormInjection = this.getWebFormInjectionOrThrow(formId);
        embedOptions = EmbedOptionsResolver.resolve(injection.rootElement, embedOptions);
        embedOptions.autoEmbed ??= true;
        embedOptions.autoLoad ??= true;
        injection.updateEmbedOptions(embedOptions);
        injection.load();
    }

    /**
     * Starts the session for a form at the specified html element, so that the user can begin interacting with it.
     * Returns a unique formId, that can be used when calling further embed initialisation functions on this form
     * instance.
     * If the app's bundle has not yet been downloaded, it will be downloaded. If the quote or claim and form
     * configuration has not be fetched, it will be fetched first.
     */
    public startFormElement(element: HTMLElement, embedOptions?: WebFormEmbedOptions): string {
        const formId: string = this.getFormId(element)
            ?? this.createFormInjection(element, embedOptions);
        this.startForm(formId, embedOptions);
        return formId;
    }

    /**
     * Starts the session for a form as a popup modal over the page, so that the user can begin interacting with it.
     * Returns a unique formId, that can be used when calling further embed initialisation functions on this form
     * instance.
     * If the app's bundle has not yet been downloaded, it will be downloaded. If the quote or claim and form
     * configuration has not be fetched, it will be fetched first.
     */
    public startFormPopup(embedOptions: WebFormEmbedOptions): string {
        const divEl: HTMLDivElement = this.createPopupDiv();
        embedOptions.modalPopup = true;
        return this.startFormElement(divEl, embedOptions);
    }

    /**
     * Starts the session for a form with the specified formId, so that the user can begin interacting with it.
     * If the app's bundle has not yet been downloaded, it will be downloaded. If the quote or claim and form
     * configuration has not be fetched, it will be fetched first.
     */
    public startForm(formId: string, embedOptions?: WebFormEmbedOptions): void {
        const injection: WebFormInjection = this.getWebFormInjectionOrThrow(formId);
        embedOptions = EmbedOptionsResolver.resolve(injection.rootElement, embedOptions);
        embedOptions.autoEmbed = true;
        embedOptions.autoLoad = true;
        embedOptions.autoStart = true;
        injection.updateEmbedOptions(embedOptions);
        injection.start();
    }

    /**
     * 
     * @param element the html element (e.g. div) where the form should be injected,
     * and which contains the relevant data parameters
     * @param formModel 
     * @returns the id unique to this injection
     * @deprecated use startFormElement instead
     */
    public loadWebFormElement(element: HTMLElement, embedOptions?: WebFormEmbedOptions): string {
        return this.startFormElement(element, embedOptions);
    }

    /**
     * Loads a web form in a popup modal.
     * @param embedOptions The options
     * @returns the id unique to this injection
     * @deprecated use startFormPopup instead
     */
    public loadWebForm(embedOptions: WebFormEmbedOptions): HTMLDivElement {
        const formId: string = this.startFormPopup(embedOptions);
        const el: HTMLElement | null = document.getElementById(formId);
        return <HTMLDivElement>el;
    }

    private createPortalInjections(): void {
        const elements: HTMLCollectionOf<Element> = document.getElementsByClassName('ubind-portal');
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i: number = 0; i < elements.length; i++) {
            const element: HTMLElement = <HTMLElement>elements[i];
            const autoEmbed: boolean = element.getBooleanAttributeValue('data-auto-embed', true);
            if (autoEmbed) {
                this.loadPortalElement(element);
            }
        }
    }

    public loadPortalElement(element: HTMLElement, embedOptions?: PortalEmbedOptions): string {
        if (!embedOptions) {
            embedOptions = <PortalEmbedOptions>{};
        }

        if (!element.id) {
            element.id = 'ubind-portal';
        }

        if (embedOptions.fullScreen == null && embedOptions.fullScreen == undefined) {
            embedOptions.fullScreen = true;
        }

        embedOptions.tenant = embedOptions.tenant
            ?? element.getStringAttributeValueOrNull('data-tenant') ?? undefined
            ?? element.getStringAttributeValueOrNull('data-tenant-id') ?? undefined;
        embedOptions.organisation
            = embedOptions.organisation ?? element.getStringAttributeValueOrNull('data-organisation') ?? undefined;
        embedOptions.environment
            = embedOptions.environment ?? element.getStringAttributeValueOrNull('data-environment') ?? undefined;
        embedOptions.portal = embedOptions.portal
            ?? element.getStringAttributeValueOrNull('data-portal') ?? undefined
            ?? element.getStringAttributeValueOrNull('data-portal-id') ?? undefined;
        embedOptions.path = embedOptions.path ?? this.getPortalPath(element);
        this.overrideEnvironmentBasedOnPath(embedOptions);
        const parentUrl: string = this.getParentUrl();
        const injection: PortalInjection = new PortalInjection(
            element, embedOptions, parentUrl, this.injectionContext);
        const frameId: string = injection.getIframeId();
        this.injections.set(frameId, injection);
        element.setAttribute('data-injection-id', frameId);
        return frameId;
    }

    public loadPortal(embedOptions?: PortalEmbedOptions): HTMLDivElement {
        const divEl: HTMLDivElement = document.createElement('div');
        divEl.id = 'ubind-portal';
        document.body.appendChild(divEl);
        this.loadPortalElement(divEl, embedOptions);
        return divEl;
    }

    private overrideEnvironmentBasedOnPath(embedOptions: PortalEmbedOptions): void {
        if (embedOptions.path) {
            const map: Map<string, string> = UrlHelper.gatherQueryParamsFromIncompletePath(embedOptions.path);
            const environment: string | undefined = map.get("environment");
            if (environment) {
                embedOptions.environment = environment;
            }
        }
    }

    private getPortalPath(element: HTMLElement): string {
        let path: string | null = element.getStringAttributeValueOrNull('data-path');
        if (!path) {
            const currentUrl: string = window.location.search;
            path = currentUrl.includes('?path') ? currentUrl.split('path=')[1] : currentUrl;
            if (!path.includes('?')) {
                path = path.replace('&', '?');
            }
        }
        if (path && path.startsWith('/')) {
            path = path.substring(1);
        }
        return path;
    }

    private getWebFormInjectionOrThrow(formId: string): WebFormInjection {
        const injection: Injection | undefined = this.injections.get(formId);
        if (!injection) {
            throw new Error('No form found with formId \'' + formId + '\'');
        }
        if (!(injection instanceof WebFormInjection)) {
            throw new Error('The form with formId \'' + formId + '\' is not a web form');
        }
        return injection;
    }

    private handleFrameLoadingEvent(data: InterFrameMessage): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (injection) {
            injection.handleFrameLoadingEvent(data);
        } else {
            console.error('Received a frame loading event for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        }
    }

    private handleAppLoadEvent(data: InterFrameMessage): void {
        document.removeEventListener('keydown', this.onXKeydownRevealIFrame);
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (injection) {
            injection.handleAppLoadEvent(data);
        } else {
            console.error('Received an app load event for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        }
    }

    private handleConfigurationLoadedEvent(data: InterFrameMessage): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (injection != null && this.isWebFormInjection(injection)) {
            (<WebFormInjection>injection).handleConfigurationLoadedEvent(data);
        } else {
            console.error('Received a configuration loaded event for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        }
    }

    private handleWebFormLoadEvent(data: InterFrameMessage): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (injection != null && this.isWebFormInjection(injection)) {
            (<WebFormInjection>injection).handleWebFormLoadEvent(data);
        } else {
            console.error('Received an webFormLoad event for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        }
    }

    private handleFormSessionReadyEvent(data: InterFrameMessage): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (injection != null && this.isWebFormInjection(injection)) {
            (<WebFormInjection>injection).handleFormSessionReadyEvent(data);
        } else {
            console.error('Received a formSessionReady event for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        }
    }

    private handleWebFormPolicyTransactionEvent(origin: any, data: any): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (injection != null && this.isWebFormInjection(injection)) {
            (<WebFormInjection>injection).handlePolicyTransactionEvents(
                data.messageType, origin, data?.payload?.data);
        } else {
            console.error('Received a completed policy transaction event for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        }
    }

    private handleWebFormPageChangeEvent(origin: any, data: any): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (injection != null && this.isWebFormInjection(injection)) {
            (<WebFormInjection>injection).handlePageChangedEvent(origin, data?.payload?.data);
        } else {
            console.error('Received a page change event for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        }
    }

    private isWebFormInjection(instance: Injection): instance is WebFormInjection {
        return (<any>instance)['handleWebFormLoadEvent'] != null;
    }

    private handleScrollToElementEvent(data: any): void {
        const injection: Injection | undefined = this.injections.get(data.frameId);
        if (!injection) {
            console.error('Received a scroll to element event for ubind injection iframe with id \''
                + data.frameId + '\' however no ubind injection iframe with that id was found.');
        } else if (injection instanceof WebFormInjection) {
            (<WebFormInjection>injection).handleScrollToElementEvent(data);
        } else {
            console.error('Received a scroll to element event for ubind injection iframe with id \''
                + data.frameId
                + '\' however that ubind iframe id refers to a non web form iframe (e.g. a  portal iframe).');
        }
    }

    private removeToken(): void {
        // is not in iframe
        if (window.self === window.top) {
            sessionStorage.removeItem('ubind.accessToken');
        }
    }

    private onXKeydownRevealIFrame(event: KeyboardEvent): void {
        if (event.key == 'd' && event.ctrlKey && event.altKey) {
            this.injections.forEach((injection: Injection) => {
                if (this.isWebFormInjection(injection)) {
                    (<WebFormInjection>injection).handleWebFormLoadEvent(<InterFrameMessage>{
                        payload: {
                            status: 'success',
                        },
                    });
                    (<WebFormInjection>injection).handleDebuggingMode();
                }
            });
        }
    }

    private getParentUrl(): string {
        let parentUrl: string = document.location.href;
        const queryStringStart: number = parentUrl.indexOf('?');
        if (queryStringStart > 0) {
            parentUrl = parentUrl.substring(0, queryStringStart);
        }
        return parentUrl;
    }
}

if (_uBindInjector == null) {
    _uBindInjector = new UBindInjector();
    (<any>window)['_uBindInjector'] = _uBindInjector;
    _uBindInjector.detectAndInject();
}
